import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../api/api";
import useUrlQuery from "../hooks/useUrlQuery";
import { useGetProposalDetailsQuery } from "./Proposal.service";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useState } from "react";
interface PanCKYCReq {
  pan_no: string | undefined;
  PROPOSAL_ID?: string;
  dob?: string;
  INSURANCE_SLUG?: string;
}
export const useVerifyPanCKYC = () => {
  const { product } = useGetProposalDetailsQuery();
  const queryClient = useQueryClient();
  const { getUrlQuery } = useUrlQuery();
  const proposal_id = getUrlQuery("proposal_id");

  const { mutateAsync, isPending, reset } = useMutation({
    mutationFn: (data: PanCKYCReq) => {
      return api.post(
        data?.INSURANCE_SLUG === "star"
          ? "api/VerifyStarCKYC"
          : "api/GetHdfcCKYCDetails",
        data
      );
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["proposal", proposal_id],
      });
    },

    onError: (err: any) => {
      if (err?.response?.status === 500) {
        if (err?.response?.data?.showpopup) {
          Swal.fire("Alert", err?.response?.data?.message);
        } else {
          toast.error(
            err?.response?.data?.message ||
            "Something went wrong while submitting the proposal"
          );
        }
      } else {
        toast.error("An unexpected error occurred");
      }
    },
  });

  return {
    verifyCKYC: mutateAsync,
    isVerifyCKYC: isPending,
    resetVerify: reset,
  };
};

// Define the types for the response as described earlier
interface CKYCResponseData {
  verification_status: boolean;
  ckycNumber: number;
  ckycReferenceId: string;
  proposer_address_proof: string;
  proposer_photo: string;
  proposer_form_60: string;
  status: number;
}

interface APIResponse {
  message: string;
  status: number;
  return_data: CKYCResponseData;
}

export const useGenerateCKYC = () => {
  const [returnData, setReturnData] = useState<string | null>(null);
  const [photo, setPhoto] = useState<string | null>(null);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post("api/GenerateStarCKYC", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response?.data?.return_data) {
        setReturnData(response.data.return_data.proposer_address_proof);
        setPhoto(response.data.return_data.proposer_photo);
        return response.data.return_data;
      }

      throw new Error("Invalid response data");
    },

    onError: (err: any) => {
      if (err?.response?.status === 500) {
        if (err?.response?.data?.showpopup) {
          Swal.fire(err?.response?.data?.message);
        } else {
          toast.error(
            err?.response?.data?.message ||
            "Something went wrong while submitting the proposal"
          );
        }
      } else {
        toast.error("An unexpected error occurred");
      }
    },
  });

  return {
    generateCKYC: mutateAsync,
    isGeneratingCKYC: isPending,
    generateCkycData: returnData,
    photo,
  };
};

export const useUploadCignaDocMutation = () => {
  const { product } = useGetProposalDetailsQuery();
  const { getUrlQuery } = useUrlQuery();
  const proposal_id = getUrlQuery("proposal_id");


  const { mutateAsync } = useMutation({
    mutationFn: async (data: any) => {
      try {

        const formData = new FormData();

        formData.append("proposal_id", proposal_id);

        // ✅ Ensure file is correct (important)
        formData.append(
          "proposer_address_proof",
          data?.proposer_address_proof instanceof File
            ? data.proposer_address_proof
            : data?.proposer_address_proof?.[0]
        );

        formData.append(
          "proposer_identity_proof",
          data?.proposer_identity_proof instanceof File
            ? data.proposer_identity_proof
            : data?.proposer_identity_proof?.[0]
        );

        const response = await api.post(
          product?.INSURANCE_SLUG?.includes("care")
            ? "api/UploadCareCKYCDoc"
            : "api/UploadCignaCKYCDoc",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );


        const res = response.data;

        // // ✅ Handle backend logical error (even if 200)
        // if (res?.status === 500 && res?.showpopup) {
        //   Swal.fire("Oops", res?.message);
        //   return;
        // }

        // if (res?.status !== 200) {
        //   throw new Error(res?.message || "Upload failed");
        // }

        return res;

      } catch (error: any) {
        console.log("Upload Error:", error);

        const errRes = error?.response?.data;

        if (errRes?.status === 500 && errRes?.showpopup) {
          Swal.fire("Oops", errRes?.message);
          return;
        }

        Swal.fire("Error", errRes?.message || "Something went wrong");

        // throw error;
      }
    },
  });

  return {
    upload: mutateAsync,
  };
};

export const useVerifyCareCKYC = () => {
  const { getUrlQuery } = useUrlQuery();
  const proposal_id = getUrlQuery("proposal_id");

  const { mutateAsync, isPending, reset, data } = useMutation({
    mutationFn: async (payload: PanCKYCReq) => {
      const res = await api.post("api/VerifyCareCKYC", {
        ...payload,
        proposal_id,
      });

      const response = res.data;
      if (response?.status === 500) {
        toast.error(response?.message || "Verification failed");
      }
      return res;
    },

    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  return {
    verifyCareCKYC: mutateAsync,
    isVerifyCareCKYC: isPending,
    resetVerify: reset,
    ckycData: data,
    referenceId: data?.data?.return_data?.referenceId,
  };
};
